import React, { useState, useEffect, useCallback } from 'react';
import { CheckSquare, Clock, AlertTriangle, Plus, RefreshCw, ChevronDown } from 'lucide-react';
import { tasksApi, ComplianceTask, TaskStats } from '../api/tasks.api';
import { useAuth } from '@contexts/AuthContext';

const priorityColors: Record<string, string> = {
  low: 'bg-green-100 text-green-800',
  medium: 'bg-yellow-100 text-yellow-800',
  high: 'bg-orange-100 text-orange-800',
  critical: 'bg-red-100 text-red-800',
};

const statusColors: Record<string, string> = {
  pending: 'bg-gray-100 text-gray-800',
  in_progress: 'bg-blue-100 text-blue-800',
  completed: 'bg-green-100 text-green-800',
  overdue: 'bg-red-100 text-red-800',
};

const STATUSES = ['', 'pending', 'in_progress', 'completed', 'overdue'];
const PRIORITIES = ['', 'low', 'medium', 'high', 'critical'];

const TaskDashboard: React.FC = () => {
  const { user } = useAuth();
  const [tasks, setTasks] = useState<ComplianceTask[]>([]);
  const [stats, setStats] = useState<TaskStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [statusModalTask, setStatusModalTask] = useState<ComplianceTask | null>(null);
  const [newStatus, setNewStatus] = useState('');
  const [form, setForm] = useState({ title: '', due_date: '', priority: 'medium', assigned_to: '', description: '' });
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    setIsLoading(true);
    setError('');
    try {
      const [tasksRes, statsRes] = await Promise.all([
        tasksApi.listTasks({ ...(statusFilter ? { status: statusFilter } : {}), ...(priorityFilter ? { priority: priorityFilter } : {}), limit: 50 }),
        tasksApi.getStats(user?.company_id ?? undefined),
      ]);
      setTasks(tasksRes.tasks || []);
      setStats(statsRes);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setIsLoading(false);
    }
  }, [statusFilter, priorityFilter, user?.company_id]);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    if (!form.title.trim()) return;
    setSubmitting(true);
    try {
      const taskData: Partial<ComplianceTask> = { title: form.title, priority: form.priority as any };
      if (form.due_date) taskData.due_date = form.due_date;
      if (form.assigned_to) taskData.assigned_to = Number(form.assigned_to);
      if (form.description) taskData.description = form.description;
      if (user?.company_id != null) taskData.company_id = user.company_id;
      await tasksApi.createTask(taskData);
      setShowModal(false);
      setForm({ title: '', due_date: '', priority: 'medium', assigned_to: '', description: '' });
      load();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleStatusUpdate = async () => {
    if (!statusModalTask || !newStatus) return;
    setSubmitting(true);
    try {
      await tasksApi.updateStatus(statusModalTask.id, newStatus);
      setStatusModalTask(null);
      load();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const isOverdue = (task: ComplianceTask) =>
    task.due_date && new Date(task.due_date) < new Date() && task.status !== 'completed';

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Compliance Tasks</h1>
        <div className="flex gap-2">
          <button onClick={load} className="flex items-center gap-1 px-3 py-2 text-sm text-gray-600 border rounded-lg hover:bg-gray-50">
            <RefreshCw size={16} /> Refresh
          </button>
          <button onClick={() => setShowModal(true)} className="flex items-center gap-1 px-3 py-2 text-sm text-white bg-blue-600 rounded-lg hover:bg-blue-700">
            <Plus size={16} /> Create Task
          </button>
        </div>
      </div>

      {error && <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">{error}</div>}

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {[
            { label: 'Total', value: stats.total, icon: CheckSquare, color: 'text-blue-600' },
            { label: 'Pending', value: stats.pending, icon: Clock, color: 'text-gray-600' },
            { label: 'In Progress', value: stats.in_progress, icon: ChevronDown, color: 'text-blue-500' },
            { label: 'Completed', value: stats.completed, icon: CheckSquare, color: 'text-green-600' },
            { label: 'Overdue', value: stats.overdue, icon: AlertTriangle, color: 'text-red-600' },
          ].map(({ label, value, icon: Icon, color }) => (
            <div key={label} className="bg-white border rounded-xl p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-500">{label}</span>
                <Icon size={18} className={color} />
              </div>
              <p className="text-2xl font-bold text-gray-900">{value}</p>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="flex gap-3">
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="border rounded-lg px-3 py-2 text-sm">
          {STATUSES.map(s => <option key={s} value={s}>{s ? s.replace('_', ' ') : 'All Statuses'}</option>)}
        </select>
        <select value={priorityFilter} onChange={e => setPriorityFilter(e.target.value)} className="border rounded-lg px-3 py-2 text-sm">
          {PRIORITIES.map(p => <option key={p} value={p}>{p || 'All Priorities'}</option>)}
        </select>
      </div>

      {/* Tasks Table */}
      <div className="bg-white border rounded-xl overflow-hidden">
        {isLoading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
          </div>
        ) : tasks.length === 0 ? (
          <div className="py-12 text-gray-500">No tasks found.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                {['Title', 'Priority', 'Due Date', 'Assigned To', 'Status', 'Actions'].map(h => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y">
              {tasks.map(task => (
                <tr key={task.id} className={isOverdue(task) ? 'bg-red-50' : ''}>
                  <td className="px-4 py-3 text-left font-medium text-gray-900">{task.title}</td>
                  <td className="px-4 py-3 text-left">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${priorityColors[task.priority]}`}>
                      {task.priority}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-left text-gray-600">
                    {task.due_date ? new Date(task.due_date).toLocaleDateString() : '-'}
                  </td>
                  <td className="px-4 py-3 text-left text-gray-600">{task.assigned_to_name || task.assigned_to || '-'}</td>
                  <td className="px-4 py-3 text-left">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusColors[task.status]}`}>
                      {task.status.replace('_', ' ')}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-left">
                    <button
                      onClick={() => { setStatusModalTask(task); setNewStatus(task.status); }}
                      className="text-blue-600 hover:underline text-xs"
                    >
                      Update Status
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Create Task Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md space-y-4">
            <h2 className="text-lg font-semibold">Create Task</h2>
            <input placeholder="Title *" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })}
              className="w-full border rounded-lg px-3 py-2 text-sm" />
            <textarea placeholder="Description" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}
              className="w-full border rounded-lg px-3 py-2 text-sm" rows={3} />
            <div className="grid grid-cols-2 gap-3">
              <select value={form.priority} onChange={e => setForm({ ...form, priority: e.target.value })}
                className="border rounded-lg px-3 py-2 text-sm">
                {['low', 'medium', 'high', 'critical'].map(p => <option key={p} value={p}>{p}</option>)}
              </select>
              <input type="date" value={form.due_date} onChange={e => setForm({ ...form, due_date: e.target.value })}
                className="border rounded-lg px-3 py-2 text-sm" />
            </div>
            <input placeholder="Assigned To (User ID)" value={form.assigned_to} onChange={e => setForm({ ...form, assigned_to: e.target.value })}
              className="w-full border rounded-lg px-3 py-2 text-sm" type="number" />
            <div className="flex gap-3 justify-end">
              <button onClick={() => setShowModal(false)} className="px-4 py-2 text-sm border rounded-lg">Cancel</button>
              <button onClick={handleCreate} disabled={submitting || !form.title.trim()}
                className="px-4 py-2 text-sm text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50">
                {submitting ? 'Creating...' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Status Update Modal */}
      {statusModalTask && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-sm space-y-4">
            <h2 className="text-lg font-semibold">Update Status</h2>
            <p className="text-sm text-gray-600">{statusModalTask.title}</p>
            <select value={newStatus} onChange={e => setNewStatus(e.target.value)} className="w-full border rounded-lg px-3 py-2 text-sm">
              {['pending', 'in_progress', 'completed'].map(s => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
            </select>
            <div className="flex gap-3 justify-end">
              <button onClick={() => setStatusModalTask(null)} className="px-4 py-2 text-sm border rounded-lg">Cancel</button>
              <button onClick={handleStatusUpdate} disabled={submitting}
                className="px-4 py-2 text-sm text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50">
                {submitting ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TaskDashboard;
